/**
 * @ipc/admin-ui · entities/tenant · api — вызовы /admin/tenants*.
 *
 * Список/карточка — tenants.read. POST/PATCH — tenants.manage.
 * Аналитика и админы тенанта — bridge-эндпоинты backend
 * (JWT home-tenant, runWithTenant внутри сервиса).
 */
import {
  request,
  isPlainObject,
  str,
  bool,
  num,
  strList,
  mapList,
  pageSearchParams,
  type PageInfo,
} from '@/shared/api';
import type {
  Tenant,
  TenantMulticard,
  UpdateTenantInput,
  CreateTenantInput,
  CreateTenantResult,
} from '../model/types';
import type { SummaryResponse, TimeseriesResponse, BreakdownResponse } from '@/entities/analytics';
import {
  toSummaryResponse,
  toTimeseriesResponse,
  toBreakdownResponse,
} from '@/entities/analytics/model/mappers';
import type { AdminUser, RoleOption, CreateAdminUserDto, UpdateAdminUserDto } from '@/entities/admin-user';

function toMulticard(raw: unknown): TenantMulticard {
  const obj = isPlainObject(raw) ? raw : {};
  const storeIdRaw = obj['storeId'];
  return {
    sandbox: bool(obj['sandbox']),
    applicationId: str(obj['applicationId']),
    ...(typeof storeIdRaw === 'number' && Number.isFinite(storeIdRaw)
      ? { storeId: storeIdRaw }
      : {}),
  };
}

/** Маппер одной строки тенанта. Требует непустой slug — иначе элемент отбрасывается. */
export function toTenant(raw: unknown): Tenant | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const slug = str(raw['slug']);
  if (slug.length === 0) {
    return null;
  }
  return {
    slug,
    name: str(raw['name']),
    domains: strList(raw['domains']),
    default: bool(raw['default']),
    dbUrl: str(raw['dbUrl']),
    apiKey: str(raw['apiKey']),
    logoUrl: str(raw['logoUrl']),
    multicard: toMulticard(raw['multicard']),
  };
}

/** GET /admin/tenants → список тенантов (замаскированные секреты). */
export async function fetchTenants(): Promise<Tenant[]> {
  const { data } = await request('/admin/tenants');
  return mapList(data, toTenant);
}

/** GET /admin/tenants/:slug */
export async function fetchTenant(slug: string): Promise<Tenant> {
  const { data } = await request(`/admin/tenants/${encodeURIComponent(slug)}`);
  const tenant = toTenant(data);
  if (tenant === null) {
    throw new Error(`Ответ /admin/tenants/${slug} не соответствует контракту`);
  }
  return tenant;
}

/** PATCH /admin/tenants/:slug */
export async function updateTenant(slug: string, body: UpdateTenantInput): Promise<Tenant> {
  const { data } = await request(`/admin/tenants/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body,
  });
  const tenant = toTenant(data);
  if (tenant === null) {
    throw new Error(`Ответ PATCH /admin/tenants/${slug} не соответствует контракту`);
  }
  return tenant;
}

/** POST /admin/tenants — создать тенанта (БД + registry в platform). */
export async function createTenant(body: CreateTenantInput): Promise<CreateTenantResult> {
  const { data } = await request('/admin/tenants', {
    method: 'POST',
    body,
    idempotent: true,
  });
  if (!isPlainObject(data)) {
    throw new Error('Ответ POST /admin/tenants не соответствует контракту');
  }
  const tenantRaw = data['tenant'] ?? data;
  const tenant = toTenant(tenantRaw);
  if (tenant === null) {
    throw new Error('Ответ POST /admin/tenants: tenant DTO невалиден');
  }
  const plainApiKey =
    typeof data['plainApiKey'] === 'string' && data['plainApiKey'].length > 0
      ? data['plainApiKey']
      : undefined;
  return {
    tenant,
    ...(plainApiKey !== undefined ? { plainApiKey } : {}),
  };
}

export interface TenantAnalyticsFilters {
  preset?: string;
  from?: string;
  to?: string;
  merchantId?: number;
  provider?: string;
  status?: string;
  ps?: string;
  groupBy?: string;
}

function analyticsParams(filters: TenantAnalyticsFilters): Record<string, string | number | undefined> {
  return {
    preset: filters.preset,
    from: filters.from,
    to: filters.to,
    merchantId: filters.merchantId,
    provider: filters.provider,
    status: filters.status,
    ps: filters.ps,
  };
}

export async function fetchTenantAnalyticsSummary(
  slug: string,
  filters: TenantAnalyticsFilters,
): Promise<SummaryResponse> {
  const res = await request(`/admin/tenants/${encodeURIComponent(slug)}/analytics/summary`, {
    searchParams: analyticsParams(filters),
  });
  const summary = toSummaryResponse(res.data);
  if (summary === null) {
    throw new Error('Ответ analytics/summary тенанта не соответствует контракту');
  }
  return summary;
}

export async function fetchTenantAnalyticsTimeseries(
  slug: string,
  filters: TenantAnalyticsFilters,
): Promise<TimeseriesResponse> {
  const res = await request(`/admin/tenants/${encodeURIComponent(slug)}/analytics/timeseries`, {
    searchParams: analyticsParams(filters),
  });
  const series = toTimeseriesResponse(res.data);
  if (series === null) {
    throw new Error('Ответ analytics/timeseries тенанта не соответствует контракту');
  }
  return series;
}

export async function fetchTenantAnalyticsBreakdown(
  slug: string,
  filters: TenantAnalyticsFilters & { groupBy: string },
): Promise<BreakdownResponse> {
  const res = await request(`/admin/tenants/${encodeURIComponent(slug)}/analytics/breakdown`, {
    searchParams: { ...analyticsParams(filters), groupBy: filters.groupBy },
  });
  const breakdown = toBreakdownResponse(res.data);
  if (breakdown === null) {
    throw new Error('Ответ analytics/breakdown тенанта не соответствует контракту');
  }
  return breakdown;
}

function toAdminUser(raw: unknown): AdminUser | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const id = num(raw['id'], -1);
  const tgUsername = str(raw['tgUsername']);
  if (id < 0 || tgUsername.length === 0) {
    return null;
  }
  const roleDisplayName = typeof raw['roleDisplayName'] === 'string' ? raw['roleDisplayName'] : null;
  return {
    id,
    tgUsername,
    telegramId: typeof raw['telegramId'] === 'number' ? raw['telegramId'] : null,
    roleId: num(raw['roleId']),
    roleDisplayName,
    isActive: bool(raw['isActive']),
    createdAt: str(raw['createdAt']),
  };
}

function toRoleOption(raw: unknown): RoleOption | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  const id = num(raw['id'], -1);
  const displayName = str(raw['displayName']);
  if (id < 0 || displayName.length === 0) {
    return null;
  }
  return { id, displayName };
}

export interface TenantAdminsPage {
  items: AdminUser[];
  pagination: PageInfo;
}

export async function fetchTenantAdmins(
  slug: string,
  filters: { page: number; limit: number; search?: string },
): Promise<TenantAdminsPage> {
  const searchParams: Record<string, string | number> = {
    ...pageSearchParams({ page: filters.page, limit: filters.limit }),
  };
  if (filters.search !== undefined && filters.search.length > 0) {
    searchParams['search'] = filters.search;
  }
  const { data, meta } = await request(`/admin/tenants/${encodeURIComponent(slug)}/admins`, {
    searchParams,
  });
  const items = mapList(data, toAdminUser);
  const pagination: PageInfo = meta?.pagination ?? {
    page: filters.page,
    limit: filters.limit,
    total: items.length,
    totalPages: 1,
  };
  return { items, pagination };
}

export async function createTenantAdmin(slug: string, body: CreateAdminUserDto): Promise<AdminUser> {
  const { data } = await request(`/admin/tenants/${encodeURIComponent(slug)}/admins`, {
    method: 'POST',
    body,
    idempotent: true,
  });
  const row = toAdminUser(data);
  if (row === null) {
    throw new Error('Ответ create admin тенанта не соответствует контракту');
  }
  return row;
}

export async function updateTenantAdmin(
  slug: string,
  id: number,
  body: UpdateAdminUserDto,
): Promise<AdminUser> {
  const { data } = await request(`/admin/tenants/${encodeURIComponent(slug)}/admins/${id}`, {
    method: 'PATCH',
    body,
  });
  const row = toAdminUser(data);
  if (row === null) {
    throw new Error('Ответ update admin тенанта не соответствует контракту');
  }
  return row;
}

export async function fetchTenantRoles(slug: string): Promise<RoleOption[]> {
  const { data } = await request(`/admin/tenants/${encodeURIComponent(slug)}/roles`);
  return mapList(data, toRoleOption);
}
